import React, { useEffect, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Col,
  Empty,
  Input,
  Row,
  Skeleton,
  Table,
  Tabs,
} from "antd";
import { map, get } from "lodash";

import { recordError } from "../../../utils/logService";
import api from "../../../services/finYearService";
import EditFinYear from "./EditFinYear";
import moment from "moment";

import {
  Messages,
  Notify,
  openNotification,
  util
} from '@seims/shared';

const FinYearList = ({ hasUpdated, setHasUpdated }) => {
  const [actionLabel, setActionLabel] = useState("Activate");
  const [visible, setVisible] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [finYears, setFinYears] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedFinYear, setSelectedFinYear] = useState({});
  const [activeFinYear, setActiveFinYear] = useState({});

  const entity = "Financial Year";
  const currentDate = moment().format("YYYY-MM-DD");

  useEffect(() => {
    if (hasUpdated === true) {
      getFinancialYears();
      getActiveFinancialYear();
    }
  }, [hasUpdated]);

  let msg = "";

  const showModal = (record) => {
    setVisible(record);
    setSelectedFinYear(record);
  };

  const columns = [
    {
      title: "Financial Year",
      dataIndex: "finYear",
      key: "1",
      showOnResponse: true,
      showOnDesktop: true,
      sorter: (a, b) => a.finYear.localeCompare(b.finYear),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "2",
      showOnResponse: true,
      showOnDesktop: true,
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "3",
      showOnResponse: true,
      showOnDesktop: true,
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "4",
      showOnResponse: true,
      showOnDesktop: true,
      render: (value) =>
        value == "Yes" ? (
          <CheckOutlined
            style={{
              fontSize: "18px",
              color: "#04a596",
              fontWeight: "bold",
            }}
          />
        ) : (
          <CloseOutlined
            style={{
              fontSize: "18px",
              color: "#a21b1b",
              fontWeight: "bold",
            }}
          />
        ),
      sorter: (a, b) => a.active.localeCompare(b.active),
    },
    {
      title: "Id",
      dataIndex: "id",
      key: "4",
      showOnResponse: true,
      showOnDesktop: true,
    },
    // {
    //   title: "Actions",
    //   dataIndex: "edit",
    //   showOnResponse: true,
    //   showOnDesktop: true,
    //   render: (_, record) => {

    //     /// This rule allows for only the next financial year to be activated
    //     /// If there are more future fin years then the activate button needs
    //     /// To be controlled accordingly based on business rules
    //     /// In cases where one makes a mistake, you should be able to still activate the current fin year based on date
    //     if ((record.active == 'No' && record.startDate >= activeFinYear.startDate) || (record.active == 'No' && currentDate <= record.endDate))
    //     return (
    //       <>
    //         <a key={record.key} onClick={() => showModal(record)}>
    //           {actionLabel}
    //         </a>
    //       </>
    //     );
    //   },
    // },
  ];

  const getActiveFinancialYear = async () => {
    try {
      const response = await api.getActiveFinYear();

      if (response && (response.status == 201 || response.status == 200)) {
        setActiveFinYear(response.data);
      }
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, entity);
      openNotification(entity, msg, Notify.ERROR);
      recordError(error);
    }
  };

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  const handleInputChange = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    globalSearch(searchValue);
  };

  const getFinancialYears = async () => {
    setHasUpdated(false);
    setSearchText("");
    const entity = "FinancialYear";
    setFinYears([]);

    try {
      setLoading(true);
      const response = await api.getAllFinYears(true);

      if (response && response.status === 200) {
        const { data } = response;
        const sorted = data.sort((a, b) => a.value.localeCompare(b.value));
        var finYears = sorted?.map((element) => ({
          key: element.id + "X" + element.id.toString(),
          id: element.id,
          finYear: element.shortValue ?? "0000",
          startDate: element.startDate,
          endDate: element.endDate,
          active: element.active ? "Yes" : "No",
        }));
        setFinYears(finYears);
        setFilteredData(finYears);
      }
      setLoading(false);
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, entity);
      openNotification(
        "System failed to load NYS programme",
        msg,
        Notify.ERROR
      );
      recordError(error);
    }
  };

  const globalSearch = (searchValue) => {
    if (searchText === "") {
      setFilteredData(finYears);
      return;
    }

    const reg = new RegExp(
      searchValue.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"),
      "gi"
    );

    var _filteredData = map(finYears, (record) => {
      const nameMatch = get(record, "finYear").match(reg);

      if (!nameMatch) {
        return null;
      }
      return record;
    }).filter((record) => record);

    setSearchText(searchValue);
    setFilteredData(_filteredData);
  };

  const filteredColumns = columns.filter((c) => c.dataIndex !== "id");

  return (
    <>
      <div>Financial Year List</div>
      <Row>
        <Col xs={24} md={8}>
          <Input
            placeholder="Search Financial Year"
            type="text"
            className="search-bar"
            onChange={handleInputChange}
            allowClear
            size="large"
            prefix={<SearchOutlined />}
          />
        </Col>
      </Row>
      <div className="space"></div>
      <Tabs className="" type="card" key="1" />
      <div className="space"></div>
      <Table
        dataSource={loading ? [] : filteredData}
        locale={{
          emptyText: loading ? <Skeleton active={true} /> : <Empty />,
        }}
        rowKey={(record) => record.id}
        columns={filteredColumns}
        bordered
        pagination={{ pageSize: pageSize }}
        onChange={onRowChange}
        size="middle"
        mobileBreakPoint={768}
      />

      <EditFinYear
        key={"a2"}
        show={visible}
        onShow={showModal}
        setHasUpdated={setHasUpdated}
        hasUpdated={hasUpdated}
        data={selectedFinYear}
      />
    </>
  );
};

export default FinYearList;
